import React, { useState, useEffect, useMemo } from 'react';
import { View, FlatList, SectionList, RefreshControl, Platform, TouchableOpacity } from 'react-native';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { CalendarUtils, DateData } from 'react-native-calendars';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import { YStack, XStack, Separator } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import styles from '@stylesheet';
import globalStyles from '@stylesheet';

interface CalendarEvent {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  notes?: string;
  color?: string;
  calendarId?: string;
}

interface DaySection {
  title: string;
  date: string;
  data: CalendarEvent[];
}

const BreakdownScreen = () => {
  const theme = themeSelector();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const [weekStart, setWeekStart] = useState(moment().startOf('week'));

  // Calculate month that contains the week (similar to CalendarScreen)
  const month = useMemo(() => {
    const weekStartDate = weekStart.toDate();
    return {
      year: moment(weekStartDate).year(),
      month: moment(weekStartDate).month(),
      day: moment(weekStartDate).date(),
      timestamp: weekStartDate.getTime(),
      dateString: CalendarUtils.getCalendarDateString(weekStartDate),
    } as DateData;
  }, [weekStart]);

  // Calculate week range for filtering
  const weekRange = useMemo(() => {
    const start = weekStart.clone();
    const end = weekStart.clone().endOf('week');
    return {
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    };
  }, [weekStart]);

  // Fetch calendars (same approach as CalendarScreen)
  const { data: calendars } = useQuery({
    queryKey: ['calendars'],
    queryFn: async () => {
      const response = await apis.getCalendars();
      // Access calendars the same way as CalendarScreen
      return response?.data || [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch calendar events for the month (same approach as CalendarScreen)
  const { data: events, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['calendar-breakdown', month.year, month.month],
    queryFn: async () => {
      const response = await apis.getCalendarContent(month);
      // Access events the same way as CalendarScreen
      const eventsData = response?.data?.data || [];
      return eventsData;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes (same as CalendarScreen)
    refetchOnWindowFocus: false,
  });

  // Helper function to get event color (similar to handleEventList)
  const getEventColor = useMemo(() => {
    if (!calendars || !Array.isArray(calendars)) {
      return () => (theme ? '#3b82f6' : '#60a5fa');
    }

    return (event: any) => {
      const calendarId = event.calendarId || event.calendar?._id || event.calendar?.calendarId || '';
      const calendar = calendars.find(
        (cal: any) => cal.source?.id === calendarId || cal?.id === calendarId || cal._id === calendarId
      );
      return calendar?.color || calendar?.source?.color || (theme ? '#3b82f6' : '#60a5fa');
    };
  }, [calendars, theme]);

  // Group events by day (filter for current week)
  const groupedEvents = useMemo(() => {
    if (!events || !Array.isArray(events)) return [];

    const grouped: { [key: string]: CalendarEvent[] } = {};
    
    events.forEach((event: any) => {
      // Handle event date - use startDate (same as CalendarScreen)
      const eventStartDate = event.startDate;
      if (!eventStartDate) return;

      const eventDate = moment(eventStartDate);
      const dayKey = eventDate.format('YYYY-MM-DD');

      // Only include events within the current week
      const weekStartMoment = moment(weekRange.start);
      const weekEndMoment = moment(weekRange.end);
      
      if (eventDate.isSameOrAfter(weekStartMoment, 'day') && eventDate.isSameOrBefore(weekEndMoment, 'day')) {
        if (!grouped[dayKey]) {
          grouped[dayKey] = [];
        }
        // Add color to event
        const eventWithColor = {
          ...event,
          color: getEventColor(event),
        };
        grouped[dayKey].push(eventWithColor);
      }
    });

    // Convert to array and sort by date
    const sections: DaySection[] = Object.keys(grouped)
      .sort()
      .map((dayKey) => ({
        title: moment(dayKey).format('dddd, MMM D'),
        date: dayKey,
        data: grouped[dayKey].sort((a, b) => 
          moment(a.startDate).diff(moment(b.startDate))
        ),
      }));

    // Ensure all days of the week are represented
    const allDays: DaySection[] = [];
    for (let i = 0; i < 7; i++) {
      const day = weekStart.clone().add(i, 'days');
      const dayKey = day.format('YYYY-MM-DD');
      const existingSection = sections.find(s => s.date === dayKey);
      
      if (existingSection) {
        allDays.push(existingSection);
      } else {
        allDays.push({
          title: day.format('dddd, MMM D'),
          date: dayKey,
          data: [],
        });
      }
    }

    return allDays;
  }, [events, weekStart, weekRange, getEventColor]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    setWeekStart(prev => 
      direction === 'prev' 
        ? prev.clone().subtract(1, 'week')
        : prev.clone().add(1, 'week')
    );
  };

  const renderEventItem = ({ item }: { item: any }) => {
    // Handle event dates (same format as CalendarScreen)
    const startTime = moment(item.startDate);
    const endTime = moment(item.endDate || item.startDate);
    const isAllDay = startTime.format('HH:mm') === '00:00' && endTime.format('HH:mm') === '00:00';
    const timeDisplay = isAllDay 
      ? 'All day' 
      : `${startTime.format('h:mm A')}${!startTime.isSame(endTime, 'day') ? ` - ${endTime.format('MMM D, h:mm A')}` : ` - ${endTime.format('h:mm A')}`}`;

    return (
      <View
        style={Platform.OS === 'web'
          ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginVertical: 6, padding: 16, borderRadius: 12 }
          : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginVertical: 6, padding: 16, borderRadius: 12 }]
        }
      >
        <XStack alignItems="center" gap={12}>
          <View
            style={{
              width: 4,
              height: '100%',
              backgroundColor: item.color || (theme ? '#3b82f6' : '#60a5fa'),
              borderRadius: 2,
              minHeight: 40,
            }}
          />
          <YStack flex={1} gap={4}>
            <Text
              style={{
                fontSize: 16,
                fontWeight: '600',
                color: theme ? '#1e293b' : '#f1f5f9',
              }}
            >
              {item.title || item.summary || 'Untitled Event'}
            </Text>
            <XStack alignItems="center" gap={6}>
              <MaterialIcons
                name="schedule"
                size={14}
                color={theme ? '#64748b' : '#94a3b8'}
              />
              <Text
                style={{
                  fontSize: 13,
                  color: theme ? '#64748b' : '#94a3b8',
                }}
              >
                {timeDisplay}
              </Text>
            </XStack>
            {(item.notes || item.description || item.summary) && (
              <Text
                numberOfLines={2}
                style={{
                  fontSize: 13,
                  color: theme ? '#64748b' : '#94a3b8',
                  marginTop: 4,
                }}
              >
                {item.notes || item.description || item.summary}
              </Text>
            )}
          </YStack>
        </XStack>
      </View>
    );
  };

  const renderSectionHeader = ({ section }: { section: DaySection }) => {
    const isToday = moment(section.date).isSame(moment(), 'day');
    
    return (
      <View
        style={{
          paddingVertical: 12,
          paddingHorizontal: 16,
          backgroundColor: theme ? '#f8fafc' : '#0f172a',
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e2e8f0' : '#1e293b',
        }}
      >
        <XStack alignItems="center" justifyContent="space-between">
          <XStack alignItems="center" gap={8}>
            <Text
              style={{
                fontSize: 18,
                fontWeight: '700',
                color: isToday 
                  ? (theme ? '#3b82f6' : '#60a5fa')
                  : (theme ? '#1e293b' : '#f1f5f9'),
              }}
            >
              {section.title}
            </Text>
            {isToday && (
              <View
                style={{
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  backgroundColor: theme ? '#dbeafe' : '#1e3a8a',
                  borderRadius: 4,
                }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: '600',
                    color: theme ? '#1e40af' : '#93c5fd',
                  }}
                >
                  Today
                </Text>
              </View>
            )}
          </XStack>
          <Text
            style={{
              fontSize: 14,
              color: theme ? '#64748b' : '#94a3b8',
            }}
          >
            {section.data.length} {section.data.length === 1 ? 'item' : 'items'}
          </Text>
        </XStack>
      </View>
    );
  };

  const renderEmptyDay = () => (
    <View
      style={{
        padding: 20,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text
        style={{
          fontSize: 14,
          color: theme ? '#94a3b8' : '#64748b',
          fontStyle: 'italic',
        }}
      >
        No events scheduled
      </Text>
    </View>
  );

  return (
    <DefaultScreen backButton>
      <YStack flex={1} paddingHorizontal={20} paddingTop={20}>
        {/* Week Navigation Header */}
        <XStack
          alignItems="center"
          justifyContent="space-between"
          marginBottom={20}
          paddingHorizontal={4}
        >
          <XStack alignItems="center" gap={12}>
            <TouchableOpacity onPress={() => navigateWeek('prev')}>
              <MaterialIcons
                name="chevron-left"
                size={24}
                color={theme ? '#1e293b' : '#f1f5f9'}
              />
            </TouchableOpacity>
            <YStack alignItems="center">
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                }}
              >
                {weekStart.format('MMM D')} - {weekStart.clone().endOf('week').format('MMM D, YYYY')}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: theme ? '#64748b' : '#94a3b8',
                }}
              >
                Week of {weekStart.format('MMM D')}
              </Text>
            </YStack>
            <TouchableOpacity onPress={() => navigateWeek('next')}>
              <MaterialIcons
                name="chevron-right"
                size={24}
                color={theme ? '#1e293b' : '#f1f5f9'}
              />
            </TouchableOpacity>
          </XStack>
          <TouchableOpacity onPress={() => setWeekStart(moment().startOf('week'))}>
            <MaterialIcons
              name="today"
              size={24}
              color={theme ? '#3b82f6' : '#60a5fa'}
            />
          </TouchableOpacity>
        </XStack>

        {/* Events List */}
        {isLoading ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: theme ? '#64748b' : '#94a3b8' }}>Loading...</Text>
          </View>
        ) : error ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <MaterialIcons name="error-outline" size={48} color="#f44336" />
            <Text style={{ color: '#f44336', marginTop: 12 }}>Failed to load events</Text>
          </View>
        ) : (
          <SectionList
            sections={groupedEvents}
            keyExtractor={(item, index) => `${item.id || item._id || item.title || 'event'}-${index}`}
            renderItem={renderEventItem}
            renderSectionHeader={renderSectionHeader}
            ListEmptyComponent={() => (
              <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingTop: 100 }}>
                <MaterialIcons name="event-busy" size={64} color={theme ? '#94a3b8' : '#64748b'} />
                <Text
                  style={{
                    fontSize: 16,
                    color: theme ? '#64748b' : '#94a3b8',
                    marginTop: 16,
                  }}
                >
                  No events this week
                </Text>
              </View>
            )}
            SectionSeparatorComponent={() => (
              <View style={{ height: 8 }} />
            )}
            contentContainerStyle={{ paddingBottom: 100 }}
            refreshing={isFetching}
            refreshControl={
              <RefreshControl
                refreshing={isFetching}
                onRefresh={refetch}
                tintColor={theme ? '#64748b' : '#94a3b8'}
              />
            }
            stickySectionHeadersEnabled={false}
          />
        )}
      </YStack>
    </DefaultScreen>
  );
};

export default BreakdownScreen;

